import React, { useEffect, useRef, useState } from "react";
import { Bold, Italic, Underline, Palette, RotateCcw } from "lucide-react";

// Mini-editor rich text basato su contentEditable + document.execCommand.
// Supporta bold / italic / underline / colore testo + reset formattazione.
// Le note sono salvate come HTML; il backend le sanitizza con bleach (whitelist).
// L'HTML "uscente" è semplice: <b> <i> <u> <span style="color:..."> <br>.

const COLORS = [
  { name: "Nero", hex: "#111827" },
  { name: "Rosso", hex: "#DC2626" },
  { name: "Arancio", hex: "#EA580C" },
  { name: "Giallo", hex: "#CA8A04" },
  { name: "Verde", hex: "#16A34A" },
  { name: "Azzurro", hex: "#0EA5E9" },
  { name: "Blu", hex: "#2563EB" },
  { name: "Viola", hex: "#7C3AED" },
  { name: "Magenta", hex: "#DB2777" },
  { name: "Grigio", hex: "#6B7280" },
];

function ToolbarButton({ onClick, active, title, testid, children, disabled }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testid}
      className="inline-flex items-center justify-center w-8 h-8 rounded-md border text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      style={{
        borderColor: "var(--border)",
        background: active ? "var(--primary)" : "#fff",
        color: active ? "#fff" : "var(--text)",
      }}
    >
      {children}
    </button>
  );
}

export default function RichTextEditor({ value, onChange, disabled, testid, placeholder }) {
  const ref = useRef(null);
  const [active, setActive] = useState({ bold: false, italic: false, underline: false });
  const [pickerOpen, setPickerOpen] = useState(false);

  // Carica il value iniziale solo quando il valore esterno cambia (evita reset cursor su typing).
  useEffect(() => {
    if (!ref.current) return;
    if ((value || "") !== ref.current.innerHTML) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);

  const refreshActive = () => {
    try {
      setActive({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
      });
    } catch { /* noop */ }
  };

  const exec = (cmd, val) => {
    if (disabled) return;
    ref.current?.focus();
    document.execCommand(cmd, false, val);
    onChange?.(ref.current?.innerHTML || "");
    refreshActive();
  };

  const handleInput = (e) => {
    onChange?.(e.currentTarget.innerHTML);
    refreshActive();
  };

  const handleKeyDown = (e) => {
    // Shortcuts standard: Ctrl/Cmd + B/I/U
    const mod = e.ctrlKey || e.metaKey;
    if (mod && e.key.toLowerCase() === "b") { e.preventDefault(); exec("bold"); }
    else if (mod && e.key.toLowerCase() === "i") { e.preventDefault(); exec("italic"); }
    else if (mod && e.key.toLowerCase() === "u") { e.preventDefault(); exec("underline"); }
  };

  const handlePaste = (e) => {
    // Incolla SOLO testo plain — evita di portare dentro stili dalla sorgente
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  const applyColor = (hex) => {
    exec("styleWithCSS", true);
    exec("foreColor", hex);
    setPickerOpen(false);
  };

  const clearFormat = () => {
    exec("removeFormat");
  };

  return (
    <div className="rounded-md border overflow-hidden" style={{ borderColor: "var(--border)", background: "#fff" }} data-testid={testid}>
      <div
        className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b"
        style={{ borderColor: "var(--border)", background: "var(--surface-muted)" }}
      >
        <ToolbarButton onClick={() => exec("bold")} active={active.bold} disabled={disabled} title="Grassetto (Ctrl+B)" testid={`${testid}-bold`}>
          <Bold className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => exec("italic")} active={active.italic} disabled={disabled} title="Corsivo (Ctrl+I)" testid={`${testid}-italic`}>
          <Italic className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => exec("underline")} active={active.underline} disabled={disabled} title="Sottolineato (Ctrl+U)" testid={`${testid}-underline`}>
          <Underline className="w-4 h-4" />
        </ToolbarButton>

        <div className="relative">
          <ToolbarButton onClick={() => setPickerOpen((v) => !v)} active={pickerOpen} disabled={disabled} title="Colore testo" testid={`${testid}-color-trigger`}>
            <Palette className="w-4 h-4" />
          </ToolbarButton>
          {pickerOpen && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setPickerOpen(false)} />
              <div
                className="absolute z-40 mt-1 left-0 card-shell p-2 grid grid-cols-5 gap-1 w-[160px] shadow-xl"
                data-testid={`${testid}-color-popover`}
              >
                {COLORS.map((c) => (
                  <button
                    key={c.hex}
                    type="button"
                    title={c.name}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyColor(c.hex)}
                    className="w-6 h-6 rounded border hover:scale-110 transition-transform"
                    style={{ background: c.hex, borderColor: "var(--border)" }}
                    data-testid={`${testid}-color-${c.hex.slice(1).toLowerCase()}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="w-px h-5 mx-1" style={{ background: "var(--border)" }} />
        <ToolbarButton onClick={clearFormat} disabled={disabled} title="Rimuovi formattazione" testid={`${testid}-clear`}>
          <RotateCcw className="w-4 h-4" />
        </ToolbarButton>
      </div>

      <div
        ref={ref}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onMouseUp={refreshActive}
        onKeyUp={refreshActive}
        data-placeholder={placeholder || ""}
        data-testid={`${testid}-editor`}
        className="min-h-[88px] px-3 py-2 text-sm outline-none disabled:bg-[var(--surface-muted)] [&[data-placeholder]:empty:before]:content-[attr(data-placeholder)] [&[data-placeholder]:empty:before]:text-[var(--text-muted)] [&[data-placeholder]:empty:before]:pointer-events-none"
        style={{ background: disabled ? "var(--surface-muted)" : "#fff" }}
      />
    </div>
  );
}
